import { InputLabel, TextField } from '@mui/material';
import { trim } from 'lodash';
import { memo, MouseEvent, useCallback, useMemo, useState } from 'react';
import { SimpleDialog } from 'src/components/modal';
import { Snackbar } from 'src/components/snackbar';
import {
  useClone,
  useDeleteComponent,
  useMenu,
  useModal,
  useUpdateComponent,
} from 'src/hooks';
import { IStatus } from 'src/interfaces';
import { getExpiryStatusMessage, lang } from 'src/utils';
import {
  componentConvertToDraftFromCardEvent,
  componentDuplicatedFromCard,
} from 'src/utils/google-tag-manager';

import { ComponentTableCellContent } from './ComponentTableCellContent';
import { Column, ComponentRowProps } from './ComponentTableRow.interface';
import { CustomTableRow } from './ComponentTableRow.styled';

const CustomTableCell = memo(
  ({
    children,
    width,
    onClick,
  }: {
    children: React.ReactNode;
    width: string;
    onClick: (event: MouseEvent) => void;
  }) => {
    return (
      <td
        onClick={onClick}
        style={{
          position: 'relative',
          fontSize: '14px',
          padding: '15px',
          maxWidth: width,
        }}>
        {children}
      </td>
    );
  },
);

const ComponentTableRowComponent = (props: ComponentRowProps): JSX.Element => {
  const deleteComponentModal = useModal();
  const duplicateComponentModal = useModal();
  const convertToDraftModal = useModal();
  const menu = useMenu();

  const deleteComponent = useDeleteComponent();
  const updateComponent = useUpdateComponent();
  const cloneResource = useClone();

  const [duplicatedComponentName, setDuplicatedComponentName] = useState('');
  const [deletingStatus, setDeletingStatus] = useState<{
    [key: number]: boolean;
  }>({});
  const [isDuplicating, setIsDuplicating] = useState(false);

  const shouldFetchComponents = props.row.status === IStatus.READY;

  const isExpiringSoon = useMemo(() => {
    if (!shouldFetchComponents || !props.row.end_date) return null;

    return getExpiryStatusMessage(props.row.end_date);
  }, [shouldFetchComponents, props.row.end_date]);

  const onRowClick = useCallback(
    (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const isMenuClick =
        target.closest('[data-testid="row-menu-icon"]') ||
        target.closest('.MuiPopover-root');

      if (isMenuClick) return;
      event.stopPropagation();
      props.onHighlighted(props.row);
    },
    [props],
  );

  const onMenuClick = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      menu.onOpenMenu(event.currentTarget);
    },
    [menu],
  );

  const onHandleDeleteComponentModal = useCallback(
    async (componentId: number, componentName: string) => {
      props.onHandleCloseSidePanel();
      try {
        setDeletingStatus((prevStatus) => ({
          ...prevStatus,
          [componentId]: true,
        }));

        await deleteComponent.mutateAsync({ componentId, componentName });
        deleteComponentModal.onCloseModal();
        Snackbar.success(`${componentName} ${lang.successfullyDeleted}`);
      } catch {
        setDeletingStatus((prevStatus) => ({
          ...prevStatus,
          [componentId]: false,
        }));
        Snackbar.error(lang.failedToDeleteComponent);
      } finally {
        setDeletingStatus((prevStatus) => ({
          ...prevStatus,
          [componentId]: false,
        }));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [deleteComponent, deleteComponentModal, props.onHandleCloseSidePanel],
  );

  const onHandleConvertToDraftModal = useCallback(
    async (componentId: number, componentName: string) => {
      try {
        let payload = {
          id: componentId,
          name: componentName,
          status: IStatus.DRAFT,
        };
        await updateComponent.mutateAsync(payload);
        convertToDraftModal.onCloseModal();
        Snackbar.success(
          `${componentName} ${lang.successfullyConvertedToDraft}`,
        );
        componentConvertToDraftFromCardEvent(componentId);
      } catch {
        Snackbar.error(lang.failedToConvert);
      }
    },
    [updateComponent, convertToDraftModal],
  );

  const handleDuplicateComponentsConfirm = useCallback(async () => {
    setIsDuplicating(true);
    try {
      const duplicateComponentName = trim(duplicatedComponentName);
      const result = await cloneResource.mutateAsync({
        type: 'component',
        id: props.row.id,
        name: duplicateComponentName,
      });
      componentDuplicatedFromCard(
        result.id,
        props.row.id,
        props.row.component_type?.name!,
        duplicateComponentName,
        props.row.layoutType?.name!,
      );
      Snackbar.success(`${props.row.name} ${lang.successfullyDuplicated}`);
      setDuplicatedComponentName('');
      duplicateComponentModal.onCloseModal();
    } finally {
      setIsDuplicating(false);
    }
  }, [
    cloneResource,
    duplicatedComponentName,
    props.row,
    duplicateComponentModal,
  ]);

  return (
    <>
      <CustomTableRow key={props.row.id} selected={props.isHighlighted}>
        {props.columns.map((column: Column) => (
          <CustomTableCell
            onClick={onRowClick}
            key={column.key}
            width={column.width}>
            <ComponentTableCellContent
              column={column}
              isExpiringSoon={isExpiringSoon}
              props={props}
              onMenuClick={onMenuClick}
              menu={menu}
              deleteComponentModal={deleteComponentModal}
              duplicateComponentModal={duplicateComponentModal}
              convertToDraftModal={convertToDraftModal}
            />
          </CustomTableCell>
        ))}
      </CustomTableRow>

      <SimpleDialog
        open={deleteComponentModal.open}
        actionColor="error"
        cancelText={lang.cancel}
        confirmText={lang.deleteText}
        title={lang.DeleteComponentModal.title}
        onClose={deleteComponentModal.onCloseModal}
        onClickCancel={deleteComponentModal.onCloseModal}
        subTitle={lang.DeleteComponentModal.subTitle}
        modalSize="sm"
        disabled={deletingStatus[props.row.id]}
        onClickConfirm={() =>
          onHandleDeleteComponentModal(props.row.id, props.row.name)
        }
      />

      <SimpleDialog
        open={convertToDraftModal.open}
        cancelText={lang.cancel}
        confirmText={lang.confirm}
        title={lang.convertToDraftComponentModal.title}
        onClose={convertToDraftModal.onCloseModal}
        onClickCancel={convertToDraftModal.onCloseModal}
        subTitle={lang.convertToDraftComponentModal.subTitle}
        modalSize="sm"
        onClickConfirm={() =>
          onHandleConvertToDraftModal(props.row.id, props.row.name)
        }
      />

      {duplicateComponentModal.open && (
        <SimpleDialog
          title={lang.duplicateComponent}
          open={duplicateComponentModal.open}
          confirmText={lang.create}
          isLoading={isDuplicating}
          disabled={!trim(duplicatedComponentName) || isDuplicating}
          onClose={duplicateComponentModal.onCloseModal}
          onClickCancel={duplicateComponentModal.onCloseModal}
          onClickConfirm={handleDuplicateComponentsConfirm}>
          <>
            <InputLabel>{lang.componentName}</InputLabel>
            <TextField
              inputProps={{
                'data-testid': 'duplicate-component-name-input',
              }}
              fullWidth
              onChange={(event) => {
                setDuplicatedComponentName(event.target.value);
              }}
            />
          </>
        </SimpleDialog>
      )}
    </>
  );
};

export const ComponentTableRow = memo(
  ComponentTableRowComponent,
  (prevProps, nextProps) => {
    return (
      prevProps.row.id === nextProps.row.id &&
      prevProps.isHighlighted === nextProps.isHighlighted &&
      prevProps.row.status === nextProps.row.status &&
      prevProps.row.phase === nextProps.row.phase &&
      prevProps.row.name === nextProps.row.name
    );
  },
);
